import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { formatISOTimeForDisplay } from '@plato/shared-util/time-utils';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import {
    Card,
    CardContent,
    CardDetail,
    CardFront,
    CardHeader,
    CardMainAction,
    CardStart,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import { CalendarClockIcon, RefreshCwIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmEventDTO } from '../../../server/dto/crm-event-dto.ts';
import { getEventMetadata } from '../../../utils/event-utils.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { EventDetailsDialog as NewEventDetailsDialog } from '../../events-v2/components/event-details-dialog.tsx';
import { EventDetailsDialog } from '../../events/components/event-details-dialog.tsx';
import { deriveTaskText } from '../../tasks/lib/derive-task-text.ts';

export type UpcomingEventItemProps = {
    event: CrmEventDTO;
};

/**
 * One upcoming appointment with a two-line preview of its agenda. Only the owner can
 * open it, since the dialog edits the appointment.
 */
export function UpcomingEventItem({ event }: UpcomingEventItemProps): ReactElement {
    const { t } = useTranslation('activity-feed');
    const { userId, enableNewEventPage } = useAppConfig();
    const sendEvent = useAnalytics();

    const isOwner = event.owner_id === userId;
    const title = event.title ?? t('event', { ns: 'events' });
    const recurrenceWeeks = event.recurrenceRule?.weeks ?? 0;

    const when = [event.start_time, event.end_time]
        .filter((time) => time !== undefined)
        .map((time) => formatISOTimeForDisplay(time))
        .join('–');

    const agendaText = deriveTaskText(event.agendaRich ?? null);
    const agenda = event.agendaRich
        ? [agendaText.title, agendaText.detail].filter(Boolean).join(' · ')
        : (event.agenda ?? '')
              .split('\n')
              .map((line) => line.trim())
              .filter(Boolean)
              .join(' · ');

    const showDetails = (): void => {
        sendEvent('event_clicked', getEventMetadata(event));
        void DialogStateManager.show(enableNewEventPage ? NewEventDetailsDialog : EventDetailsDialog, {
            eventId: event.event_id,
            date: event.date,
            analyticsSource: 'customer_detail',
        });
    };

    return (
        <Card>
            <CardFront>
                <CardStart render={<CalendarClockIcon />} />
                <CardContent>
                    <CardHeader>
                        <CardTitle className="truncate">{title}</CardTitle>
                        <CardSubtitle className="tabular-nums">
                            {when}
                            {recurrenceWeeks > 0 && (
                                <span className="flex items-center gap-tight">
                                    <RefreshCwIcon className="size-3 shrink-0" />
                                    {t('card_recurrence_weeks', { ns: 'events', count: recurrenceWeeks })}
                                </span>
                            )}
                        </CardSubtitle>
                    </CardHeader>
                    {agenda && <CardDetail className="line-clamp-2">{agenda}</CardDetail>}
                </CardContent>
                {isOwner && (
                    <CardMainAction aria-label={t('card__open', { name: title })} onClick={showDetails} />
                )}
            </CardFront>
        </Card>
    );
}
